import { I18n } from '@affine/i18n';
import type {
  AffineUserInfo,
  UserAvatarCacheService,
  UserListService,
  UserService,
} from '@blocksuite/affine-shared/services';
import {
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
} from '@blocksuite/affine-shared/services';
import { SignalWatcher } from '@blocksuite/global/lit';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';

export const AFFINE_MEMBER_PICKER = 'affine-member-picker';

const LOAD_MORE_THRESHOLD_PX = 56;

export function shouldLoadMoreMembers(
  scrollTop: number,
  clientHeight: number,
  scrollHeight: number
) {
  return scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX;
}

export class MemberPicker extends SignalWatcher(LitElement) {
  static override styles = css`
    :host {
      position: relative;
      display: block;
      font-family: Inter, sans-serif;
    }
    button,
    input {
      font: inherit;
    }
    .trigger {
      display: flex;
      min-width: 0;
      max-width: 168px;
      min-height: 25px;
      align-items: center;
      gap: 6px;
      padding: 2px 8px 2px 4px;
      border: 0;
      border-radius: 14px;
      background: rgb(255 255 255 / 78%);
      color: #40382c;
      cursor: pointer;
      box-shadow: 0 1px 4px rgb(0 0 0 / 14%);
      transition: opacity 120ms ease;
    }
    .avatar {
      display: grid;
      width: 19px;
      height: 19px;
      flex: 0 0 19px;
      overflow: hidden;
      place-items: center;
      border-radius: 50%;
      background: #6f7568;
      color: white;
      font-size: 9px;
      font-weight: 650;
    }
    .avatar img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .trigger-name,
    .option-name {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .trigger-name {
      font-size: 11px;
      font-weight: 520;
      line-height: 1.15;
    }
    .menu {
      position: absolute;
      right: 0;
      bottom: 34px;
      z-index: 20;
      width: 232px;
      overflow: hidden;
      border: 1px solid rgb(0 0 0 / 13%);
      border-radius: 10px;
      background: #fff;
      box-shadow: 0 14px 36px rgb(0 0 0 / 30%);
      color: #262626;
    }
    .search-wrap {
      padding: 8px;
      border-bottom: 1px solid rgb(0 0 0 / 9%);
    }
    .search {
      width: 100%;
      height: 30px;
      box-sizing: border-box;
      padding: 0 9px;
      border: 1px solid rgb(0 0 0 / 14%);
      border-radius: 7px;
      outline: none;
      background: #f8f8f8;
      color: #222;
      font-size: 12px;
    }
    .search:focus {
      border-color: #6c8ef5;
      box-shadow: 0 0 0 2px rgb(108 142 245 / 16%);
    }
    .list {
      max-height: 220px;
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 5px;
    }
    .option {
      display: flex;
      width: 100%;
      height: 34px;
      box-sizing: border-box;
      align-items: center;
      gap: 8px;
      padding: 4px 7px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #262626;
      cursor: pointer;
      text-align: left;
      content-visibility: auto;
      contain-intrinsic-size: auto 34px;
    }
    .option:hover,
    .option[data-active='true'] {
      background: #f0f2f5;
    }
    .option[data-selected='true'] {
      color: #315fca;
      font-weight: 620;
    }
    .option-name {
      flex: 1;
      font-size: 12px;
    }
    .state {
      padding: 12px 9px;
      color: #737373;
      font-size: 11px;
      text-align: center;
    }
    .more {
      min-height: 26px;
      padding: 6px 8px 8px;
      color: #777;
      font-size: 10px;
      text-align: center;
    }
  `;

  @property({ attribute: false }) accessor userListService:
    | UserListService
    | undefined;
  @property({ attribute: false }) accessor userService: UserService | undefined;
  @property({ attribute: false }) accessor avatarCache:
    | UserAvatarCacheService
    | undefined;
  @property({ type: String }) accessor selectedId = '';
  @property({ type: String, reflect: true }) accessor variant = 'default';

  @state() private accessor _activeIndex = -1;
  @state() private accessor _open = false;
  @state() private accessor _query = '';

  @query('.search') private accessor _searchInput!: HTMLInputElement;
  @query('.list') private accessor _list!: HTMLElement;

  private _searchTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly _onDocumentPointerDown = (event: PointerEvent) => {
    if (!this._open || event.composedPath().includes(this)) return;
    this._setOpen(false);
  };

  override disconnectedCallback() {
    this._removeOutsidePointerListener();
    super.disconnectedCallback();
    if (this._searchTimer !== null) clearTimeout(this._searchTimer);
  }

  private _addOutsidePointerListener() {
    if (typeof document === 'undefined') return;
    document.addEventListener('pointerdown', this._onDocumentPointerDown, true);
  }

  private _removeOutsidePointerListener() {
    if (typeof document === 'undefined') return;
    document.removeEventListener(
      'pointerdown',
      this._onDocumentPointerDown,
      true
    );
  }

  private get _members() {
    const service = this.userListService;
    if (!service) return [];
    const current = this.userService?.currentUserInfo$.value;
    const unique = new Map<string, AffineUserInfo>();
    if (current) unique.set(current.id, current);
    for (const user of service.users$.value) unique.set(user.id, user);
    return [...unique.values()].filter(user => !user.removed);
  }

  private get _selectedMember() {
    if (!this.selectedId) return null;
    return (
      this._members.find(user => user.id === this.selectedId) ??
      this.userService?.userInfo$(this.selectedId).value ??
      null
    );
  }

  private _displayName(user: AffineUserInfo | null) {
    return user && !user.removed
      ? user.name?.trim() ||
          I18n.t('com.streamlain.canvas.member-picker.unnamed-member')
      : '';
  }

  private _initial(user: AffineUserInfo | null) {
    return (this._displayName(user)[0] || '?').toUpperCase();
  }

  private _avatar(user: AffineUserInfo | null) {
    if (!user || user.removed || !user.avatar) return this._initial(user);
    return renderCachedUserAvatarUrl({
      avatarCache: this.avatarCache,
      avatarUrl: user.avatar,
      userId: user.id,
      render: (url, cached) =>
        html`<img
          src=${url}
          alt=""
          @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
          @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
        />`,
    });
  }

  private _setOpen(open: boolean) {
    this._open = open;
    if (!open) {
      this._removeOutsidePointerListener();
      return;
    }
    this._addOutsidePointerListener();
    this._query = '';
    this._activeIndex = -1;
    this.userListService?.search('');
    void this.updateComplete.then(() => this._searchInput?.focus());
  }

  private _onSearch(event: InputEvent) {
    this._query = (event.target as HTMLInputElement).value;
    this._activeIndex = -1;
    if (this._searchTimer !== null) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.userListService?.search(this._query.trim());
      this._searchTimer = null;
    }, 200);
  }

  private _onScroll(event: Event) {
    const target = event.currentTarget as HTMLElement;
    const service = this.userListService;
    if (
      service?.hasMore$.value &&
      !service.isLoading$.value &&
      shouldLoadMoreMembers(
        target.scrollTop,
        target.clientHeight,
        target.scrollHeight
      )
    ) {
      service.loadMore();
    }
  }

  private _select(memberId: string) {
    this.dispatchEvent(
      new CustomEvent('member-change', {
        bubbles: true,
        composed: true,
        detail: { memberId },
      })
    );
    this._setOpen(false);
  }

  private _onKeyDown(event: KeyboardEvent) {
    event.stopImmediatePropagation();
    const members = this._members;
    if (event.key === 'Escape') {
      event.preventDefault();
      this._setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (members.length === 0) return;
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this._activeIndex =
        (this._activeIndex + direction + members.length) % members.length;
      void this.updateComplete.then(() => {
        this._list
          ?.querySelector(`[data-index='${this._activeIndex}']`)
          ?.scrollIntoView({ block: 'nearest' });
      });
      return;
    }
    if (event.key === 'Enter' && this._activeIndex >= 0) {
      event.preventDefault();
      const member = members[this._activeIndex];
      if (member) this._select(member.id);
    }
  }

  override render() {
    const selected = this._selectedMember;
    const members = this._members;
    const loading = this.userListService?.isLoading$.value ?? false;
    const hasMore = this.userListService?.hasMore$.value ?? false;
    return html`
      <button
        class="trigger"
        part="trigger"
        type="button"
        aria-haspopup="listbox"
        aria-expanded=${this._open}
        title=${selected
          ? this._displayName(selected)
          : I18n.t('com.streamlain.canvas.member-picker.assign-member')}
        @pointerdown=${(event: Event) => event.stopPropagation()}
        @click=${() => this._setOpen(!this._open)}
      >
        <span class="avatar">${this._avatar(selected)}</span>
        <span class="trigger-name"
          >${selected
            ? this._displayName(selected)
            : I18n.t('com.streamlain.canvas.member-picker.assign')}</span
        >
      </button>
      ${this._open
        ? html`<div
            class="menu"
            part="menu"
            @pointerdown=${(event: Event) => event.stopPropagation()}
            @keydown=${this._onKeyDown}
          >
            <div class="search-wrap">
              <input
                class="search"
                type="search"
                autocomplete="off"
                placeholder=${I18n.t(
                  'com.streamlain.canvas.member-picker.search-placeholder'
                )}
                aria-label=${I18n.t(
                  'com.streamlain.canvas.member-picker.search-label'
                )}
                .value=${this._query}
                @input=${this._onSearch}
              />
            </div>
            <div
              class="list"
              role="listbox"
              aria-label=${I18n.t(
                'com.streamlain.canvas.member-picker.list-label'
              )}
              @scroll=${this._onScroll}
            >
              <button
                class="option"
                type="button"
                data-selected=${!this.selectedId}
                @click=${() => this._select('')}
              >
                <span class="avatar">–</span>
                <span class="option-name"
                  >${I18n.t(
                    'com.streamlain.canvas.member-picker.unassigned'
                  )}</span
                >
              </button>
              ${members.map(
                (member, index) =>
                  html`<button
                    class="option"
                    type="button"
                    role="option"
                    data-index=${index}
                    data-active=${index === this._activeIndex}
                    data-selected=${member.id === this.selectedId}
                    aria-selected=${member.id === this.selectedId}
                    @mouseenter=${() => (this._activeIndex = index)}
                    @click=${() => this._select(member.id)}
                  >
                    <span class="avatar">${this._avatar(member)}</span>
                    <span class="option-name"
                      >${this._displayName(member)}</span
                    >
                  </button>`
              )}
              ${!loading && members.length === 0
                ? html`<div class="state">
                    ${I18n.t('com.streamlain.canvas.member-picker.no-members')}
                  </div>`
                : nothing}
              ${loading
                ? html`<div class="state">
                    ${I18n.t('com.streamlain.canvas.member-picker.loading')}
                  </div>`
                : nothing}
              ${hasMore && !loading
                ? html`<div class="more">
                    ${I18n.t(
                      'com.streamlain.canvas.member-picker.scroll-for-more'
                    )}
                  </div>`
                : nothing}
            </div>
          </div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_MEMBER_PICKER]: MemberPicker;
  }
}
